import { createSelector } from 'reselect';
import { productCurrentProductIdSelector } from './productSelector';
import getDiscountAmountByTariffAndPeriod from '../../resolvers/getDiscountAmountByTariffAndPeriod';
import { pagesTariffsSelector } from './pagesSelectors';
import { default as _u } from 'underscore';
import { ALLOW_SHOWING_NON_APPLICABLE_FEATURED_FLAG } from '../../constants/constants';
import { getLogger } from '../../lib/devMode';
import getDiscountAmountByProduct from '../../resolvers/getDiscountAmountByProduct';
import getDiscountDaysByProduct from '../../resolvers/getDiscountDaysByProduct';
import getProductWithAppliedAccount from '../../mutators/getProductWithAppliedAccount';
import {customRound} from "../../lib/customRound";

export const cartCartSelector = (state) => state.cart;
export const cartRenewSelector = (state) => state.cart.renew;
export const cartAutoUpgradeSelector = (state) => state.cart.autoUpgrade;
export const cartTariffIdFromQuerySelector = (state) => state.cart.tariffFromQuery;
export const cartExtraFromQuerySelector = (state) => state.cart.extraFromQuery;
export const cartPeriodIdFromQuerySelector = (state) => state.cart.periodFromQuery;
export const cartFeaturedIdsFromQuerySelector = (state) => state.cart.featuredProductsFromQuery;
export const cartTariffIdBeforeAuthSelector = (state) => state.cart.tariffBeforeAuth;
export const cartPeriodIdBeforeAuthSelector = (state) => state.cart.periodBeforeAuth;
export const cartCurrencyFromQuerySelector = (state) => state.cart.currencyFromQuery;
export const cartDomainsFromQuerySelector = (state) => state.cart.domainsFromQuery;
export const cartAccountFromQuerySelector = (state) => state.cart.accountFromQuery;
export const cartCurrencySelector = (state) => state.cart.currency;
export const cartDomainsSelector = (state) => state.cart.domains;
export const cartAccountSelector = (state) => state.cart.account;
export const cartProductsSelector = (state) => state.cart.products;
export const cartFlagsApplied = (state) => state.cart.flags;
export const carSelectedCustomBills = (state) => state.cart.selectedCustomBills;
export const cartProductByIdSelector = (state, { product_id }) =>
    state.cart.products[product_id] || {};
export const cartIncludedProductsSelector = createSelector([cartProductsSelector], (products) => {
    return Object.values(products)?.filter((product) => !!product.isIncluded);
});
export const cartIncludedProductIdsSelector = createSelector(
    [cartIncludedProductsSelector],
    (products) => {
        return products?.length > 0 ? products?.map((product) => product.product_id) : null;
    }
);
export const cartMainProduct = createSelector(
    [cartCartSelector, productCurrentProductIdSelector],
    (cart, mainProductId) => {
        return cart?.products[mainProductId];
    }
);

export const cartMainCustomBills  = createSelector(
    [cartCartSelector, productCurrentProductIdSelector],
    (cart, mainProductId) => {
            return cart?.products[mainProductId];
    }
);

export const cartExtraPackageFlagSelector = createSelector(
    [cartMainProduct],
    (product) => {
        return !!product?.includeExtra;
    }
);

// export const cartExtraPackageFlagSelector = (state) => !!state?.cart?.extra;

export const cartFeaturedProductsListSelector = createSelector(
    [cartCartSelector, cartMainProduct, pagesTariffsSelector],
    (cart, mainProduct, tariffs) => {
        return Object.values(cart?.products).filter(
            (product) => product?.product_id !== mainProduct?.product_id
        );
    }
);

export const cartFeaturedProductsListSelectorIsSelected = createSelector(
    [cartFeaturedProductsListSelector],
    (products) => {
        return !!products?.filter(product => !!product.isIncluded).length;
    }
);

export const cartTotal = createSelector(
    [cartCartSelector, (state, props) => props],
    (cart, props) => {
        const customBills = cart.selectedCustomBills;
        if (customBills && Object.keys(customBills)?.length > 0) {
            return {total: customBills.cost * cart?.currency?.rate}
        }
        return calculateCartTotal(cart, props);
    }
);

let lastArgs = null;
let lastResult = null;

export const calculateCartTotal = (cart, props) => {
    if (!cart) return;

    const argsKey = JSON.stringify({
        ids: props?.product_id ? [props.product_id] : Object.keys(cart.products || {}),
        rate: cart?.currency?.rate,
    });

    if (argsKey === lastArgs) {
        return lastResult;
    }

    const total = {
        subtotal: 0,
        discount: 0,
        discount_unit: 'percent',
        total: 0,
    };

    if (props?.product_id) {
        total.subtotal += calculateProductTotal(cart?.products[props?.product_id], cart);
        total.total = calculateProductTotal(cart?.products[props?.product_id], cart, true);
        total.discount = ((total.subtotal - total.total) * 100) / total.subtotal;
    } else {
        Object.values(cart?.products)
            .filter((product) => product?.isIncluded)
            .forEach((product) => {
                total.subtotal += calculateProductTotal(product, cart);
                total.total += calculateProductTotal(product, cart, true);
            });
        total.discount = ((total.subtotal - total.total) * 100) / total.subtotal;
    }

    lastArgs = argsKey;
    lastResult = total;
    return total;
};

export const calculateProductTotal = (product, cart, withDiscount = false, tariffs = null) => {
    product = getProductWithAppliedAccount(product, cart?.account);
    let tariff = product?.tariff;
    if (!tariff) {
        tariff = !!tariffs && tariffs?.length
            ? tariffs.find((item) => {
                return item?.product_id === product?.product_id
                    && !!Object.values(item.periods).sort((a, b) => a.cost - b.cost).find((period) => {
                        return ALLOW_SHOWING_NON_APPLICABLE_FEATURED_FLAG || (
                            period?.interval_count === product?.period?.interval
                            && period?.interval === product?.period?.billing_period
                        );
                })
            })
            : { cost: 0 };
    }
    // Any non-numeric values.
    if (_u.isNumber(!tariff?.cost)) {
        tariff.cost = 0;
    }

    const currencyRate = cart?.currency?.rate || 1;
    const periodInterval = product?.period?.interval_count
        ?? product?.period?.interval
        ?? _u.find(tariff?.periods, Boolean)?.interval_count
        ?? 0;
    const extraSum = !!product?.includeExtra
        ? tariff?.cost *
              currencyRate *
              (product?.extra_package_rate ?? 0) *
              periodInterval
        : 0;


    const productSum = tariff?.cost * currencyRate * periodInterval;
    let productTotal = productSum + extraSum;

    // There can be decoupled logic into different strategies (per product ID/type).
    // At the moment the only different is SSL cert purchase, where the target case can be
    // covered just by multiplying the number of selected domains.
    if (product?.product_id === 5 && cart?.domains?.length) {
        productTotal *= cart.domains.length;
    }

    const licenseDiscount = getDiscountDaysByProduct(product)
        ? Number(getDiscountAmountByProduct(product) * currencyRate)
        : 0;
    const tariffWrapper = { cost: productTotal };

    const productDiscount = getDiscountAmountByTariffAndPeriod(tariffWrapper, product?.period, currencyRate) + licenseDiscount;

    getLogger().debug({
        debug: 'Product total calculation',
        product,
        tariff,
        tariffs,
        periodInterval,
        productTotal,
        licenseDiscount,
        productDiscount,
    });
    
     return Number(customRound(productTotal - (withDiscount ? productDiscount : 0)));
};
